<template>
    <div v-if="icon === ''" class="core-string-input-wrapper" :style="styles">
        <InputText
            v-model="value"
            ref="inputElement"
            :name="name"
            :placeholder="$t(placeholder)"
            :class="classes"
            :style="styles"
            :disabled="disabled"
            @focus="onFocus()"
            @blur="onBlur()"
        />

        <CoreInputNotification v-if="isFocused && validator !== null" :validator="validator" />
    </div>
    <div v-else class="core-string-input-wrapper" :style="styles">
        <span class="p-input-icon-right">
            <InputText
                v-model="value"
                ref="inputElement"
                :name="name"
                :placeholder="$t(placeholder)"
                :class="classes"
                :disabled="disabled"
                @focus="onFocus()"
                @blur="onBlur()"
            />
            <i :class="iconClass" />
        </span>

        <CoreInputNotification v-if="isFocused && validator !== null" :validator="validator" />
    </div>
</template>

<script>
import InputText from 'primevue/inputtext'
import CoreInputNotification from './CoreInputNotification.vue'

export default {
    name: 'CoreStringInput',

    components: {
        InputText,
        CoreInputNotification,
    },

    props: {
        modelValue: {
            type: String,
            default: '',
        },

        name: {
            type: String,
            default: '',
        },

        placeholder: {
            type: String,
            default: '',
        },

        icon: {
            type: String,
            default: '',
        },

        width: {
            type: Number,
            default: null,
        },

        validator: {
            type: Object,
            default: null,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        customNotificationMessage: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            isFocused: false,
        }
    },

    mounted() {
        if (this.validator) {
            if (this.value === null || this.value === '') {
                this.validator.$reset()
            } else {
                this.validator.$touch()
            }
        }
    },

    computed: {
        value: {
            get() {
                return this.modelValue
            },
            set(value) {
                this.$emit('update:modelValue', value)

                if (this.validator) {
                    this.validator.$touch()
                }
            },
        },

        classes() {
            return {
                'core-input': true,
                'core-valid': this.isValid,
                'core-invalid': this.isInvalid,
                'core-warning': this.isWarning,
            }
        },

        isValid() {
            if (this.validator !== null && !this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isInvalid() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'error'
            ) {
                return true
            }

            if (this.validator !== null && this.validator.$invalid && this.validator.$dirty) {
                return true
            }

            return false
        },

        isWarning() {
            if (
                this.customNotificationMessage !== null &&
                this.customNotificationMessage.severity !== undefined &&
                this.customNotificationMessage.severity === 'warn'
            ) {
                return true
            }

            return false
        },

        validatorErrors() {
            if (this.validator.$error === undefined) {
                return {}
            }
            return this.validator.$error
        },

        validatorParams() {
            if (this.validator.$params === undefined) {
                return {}
            }
            return this.validator.$params
        },

        iconClass() {
            return `core-string-input__icon pi ${this.icon}`
        },

        styles() {
            const stylesObject = {}

            if (this.width !== null && typeof this.width === 'number') {
                stylesObject.width = `${this.width}px`
            }

            return stylesObject
        },
    },

    methods: {
        onFocus() {
            this.isFocused = true
            this.$emit('focus')
        },

        onBlur() {
            this.isFocused = false
            this.$emit('blur')
        },

        focus() {
            this.$refs.inputElement.$el.focus()
        },
    },
}
</script>

<style>
.core-string-input-wrapper {
    position: relative;
    width: 100%;

    & .p-inputtext.p-component {
        font-size: var(--text-base);
    }

    & .p-input-icon-right {
        position: relative;
        width: 100%;
    }

    & > input:disabled {
        background-color: var(--color-disabled-bg);
        color: var(--text-disabled);
    }

    & .core-string-input__icon {
        position: absolute;
        top: 50%;
        right: 10px;
        transform: translateY(-50%);
        color: var(--text-color);
    }

    & .core-input.core-valid,
    & .core-input.core-valid:focus,
    & .core-input.core-valid:hover {
        border-color: var(--color-success);
    }

    & .core-input.core-invalid,
    & .core-input.core-invalid:focus,
    & .core-input.core-invalid:hover {
        border-color: var(--color-error);
    }

    & .core-input.core-warning,
    & .core-input.core-warning:focus,
    & .core-input.core-warning:hover {
        border-color: var(--color-warning);
    }
}
</style>
